import { AudioLines, Mic, MicOff, Phone, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LiveWaveform } from "@/components/ui/live-waveform";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type { CallStatus } from "../types";

interface CallControlsProps {
	isCalling: boolean;
	isMuted: boolean;
	isNoiseReductionOn: boolean;
	callStatus: CallStatus;
	callDuration: number;
	isLoading: boolean;
	onCall: () => void;
	onHangUp: () => void;
	onToggleMute: () => void;
	onToggleNoiseReduction: () => void;
	formatDuration: (s: number) => string;
}

export function CallControls({
	isCalling,
	isMuted,
	isNoiseReductionOn,
	callStatus,
	callDuration,
	isLoading,
	onCall,
	onHangUp,
	onToggleMute,
	onToggleNoiseReduction,
	formatDuration,
}: CallControlsProps) {
	if (!isCalling) {
		return (
			<div className="flex justify-center pt-1 pb-2">
				<Button
					size="icon-lg"
					onClick={onCall}
					disabled={isLoading}
					className="size-14 rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-600/25 hover:bg-emerald-700 active:scale-95 transition-all"
				>
					<Phone className="size-5" />
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center gap-3 pt-1 pb-2">
			{callStatus === "Answered" && (
				<LiveWaveform
					active
					height={28}
					barWidth={2}
					barGap={1}
					barRadius={4}
					barColor="#22c55e"
					fadeEdges
					fadeWidth={24}
					sensitivity={1.2}
					className="w-full"
				/>
			)}

			<div className="flex flex-col items-center gap-1">
				<span className="text-xs font-medium text-muted-foreground">
					{callStatus === "Answered"
						? formatDuration(callDuration)
						: callStatus || "Connecting…"}
				</span>
			</div>

			<div className="flex items-center gap-4">
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={onToggleMute}
							className={`size-11 rounded-full transition-colors ${
								isMuted
									? "bg-destructive/10 text-destructive"
									: "bg-muted/50 text-muted-foreground hover:bg-muted"
							}`}
						>
							{isMuted ? (
								<MicOff className="size-4" />
							) : (
								<Mic className="size-4" />
							)}
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						{isMuted ? "Unmute" : "Mute"}
					</TooltipContent>
				</Tooltip>

				<Button
					size="icon-lg"
					onClick={onHangUp}
					className="size-14 rounded-full bg-destructive text-white shadow-lg shadow-destructive/25 hover:bg-destructive/90 active:scale-95 transition-all"
				>
					<PhoneOff className="size-5" />
				</Button>

				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							onClick={onToggleNoiseReduction}
							className={`size-11 rounded-full transition-colors ${
								isNoiseReductionOn
									? "bg-blue-500/10 text-blue-600"
									: "bg-muted/50 text-muted-foreground hover:bg-muted"
							}`}
						>
							<AudioLines className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						{isNoiseReductionOn ? "Noise reduction on" : "Noise reduction off"}
					</TooltipContent>
				</Tooltip>
			</div>
		</div>
	);
}
